import { AgentOutputValidationError } from '../agent-output-validation-error.js'
import { MODEL_DELTA_STATE_REGISTRY_WRITERS } from '../model-delta-contract.js'
import {
  activeNpcIdsForLocation,
  knownLocationIds,
  locationConnectionPassability,
  locationMap,
  mergeNpcInitialState,
} from './location.js'

/** @typedef {Record<string, any>} AnyRecord */

const AUTHORIZED_DELTA = Symbol('visualGameAuthorizedDelta')
const DANGEROUS_KEYS = new Set(['__proto__', 'prototype', 'constructor'])
const PLAYER_KP_WRITERS = new Set([MODEL_DELTA_STATE_REGISTRY_WRITERS.playerKp])
const NPC_ACTION_KP_WRITERS = new Set(Object.values(MODEL_DELTA_STATE_REGISTRY_WRITERS))
const SUGGESTION_FIELDS = new Set([
  'valid',
  'reason',
  'statChanges',
  'flagsSet',
  'evidenceAdded',
  'objectStateChanges',
  'npcStateChanges',
  'inventoryChanges',
  'locationChange',
  'notes',
])
const INVENTORY_FIELDS = new Set(['add', 'remove'])

/**
 * Validate an untrusted model delta before any trusted scenario rule is merged into it.
 *
 * @param {'player_kp'|'npc_action_kp'|'npc_actor_candidate'} source
 * @param {AnyRecord} suggestion
 * @param {{ run: AnyRecord, scenario: AnyRecord, action?: AnyRecord|null, actor_id?: string|null }} context
 */
export function authorizeAgentDelta(source, suggestion, { run, scenario, action = null, actor_id = null }) {
  const value = requiredRecord(suggestion, `${source}.judgementSuggestion`)
  assertKnownKeys(value, SUGGESTION_FIELDS, `${source}.judgementSuggestion`)
  if (source !== 'npc_actor_candidate' && typeof value.valid !== 'boolean') {
    throw new AgentOutputValidationError(`${source}.judgementSuggestion.valid must be boolean`)
  }
  if (value.valid === false) requiredText(value.reason, `${source}.judgementSuggestion.reason`)

  const writers = writersForSource(source)
  validateStatChanges(value.statChanges, scenario, source, action)
  validateFlags(value.flagsSet, scenario, writers, source)
  validateEvidence(value.evidenceAdded, scenario, source)
  validateObjectChanges(value.objectStateChanges, run, scenario, writers, source, action)
  validateNpcChanges(value.npcStateChanges, run, scenario, writers, source, action, actor_id)
  validateInventory(value.inventoryChanges, run, scenario, source, action)
  validateLocationChanges(value, scenario, action, source)
  validateNotes(value.notes, source)
  return value
}

/**
 * Project the only state paths, stats and movement edges an NPC Actor may use
 * when constructing candidate_delta.
 * The returned value is detached from Scenario and frozen so prompt assembly cannot
 * accidentally mutate the compiled pack.
 *
 * @param {AnyRecord} scenario
 * @param {string|null} [actorNpcId]
 */
export function npcActorCandidateDeltaPolicy(scenario, actorNpcId = null) {
  const stateRegistry = scenario.stateRegistry
    .filter(entry => Array.isArray(entry.writers) && entry.writers.some(writer => NPC_ACTION_KP_WRITERS.has(writer)))
    .map(entry => Object.freeze({
      id: entry.id,
      scope: entry.scope,
      value_type: entry.valueType,
    }))
    .sort((left, right) => `${left.scope}:${left.id}`.localeCompare(`${right.scope}:${right.id}`))
  const statIds = [...new Set([
    ...Object.keys(scenario.initialStats),
    ...scenario.statDefinitions.map(definition => definition.id),
  ])].sort()
  const positionTransitions = Object.freeze(Object.fromEntries(scenario.locations.flatMap((location) => {
    const actorTransitions = freezePositionTransitions(location.positionTransitions, actorNpcId)
    return Object.keys(actorTransitions).length ? [[location.id, actorTransitions]] : []
  })))
  return Object.freeze({
    state_registry: Object.freeze(stateRegistry),
    stat_ids: Object.freeze(statIds),
    position_transitions: positionTransitions,
  })
}

function freezePositionTransitions(value, actorNpcId) {
  if (value === undefined) return Object.freeze({})
  const actorEntries = Object.entries(value)
    .filter(([npcId, graph]) => (
      (actorNpcId === null || npcId === actorNpcId) && Object.keys(graph).length > 0
    ))
  return Object.freeze(Object.fromEntries(actorEntries.map(([npcId, graph]) => [
    npcId,
    Object.freeze(Object.fromEntries(Object.entries(graph).map(([from, targets]) => [
      from,
      Object.freeze([...targets]),
    ]))),
  ])))
}

/**
 * A configured detector always needs the rule judge. Other NPC actions need it
 * only when Actor proposed an actual state mutation.
 *
 * @param {AnyRecord} action
 * @param {AnyRecord} scenario
 */
export function npcActionRequiresKp(action, scenario) {
  if (action.type === 'none') return false
  const detectorTypes = new Set(scenario.kpGuide.npcActionDetectors.map(detector => detector.actionType))
  if (detectorTypes.has(action.type)) return true
  return action.candidate_delta !== undefined && deltaMutationPaths(action.candidate_delta).size > 0
}

/**
 * @param {AnyRecord} output
 * @param {{ run: AnyRecord, scenario: AnyRecord }} context
 */
export function assertKpAgentOutputContext(output, { run, scenario }) {
  const action = requiredRecord(output.action, 'player_kp.action')
  const targets = requiredStringArray(action.targets, 'player_kp.action.targets')
  const claims = requiredStringArray(action.claims, 'player_kp.action.claims')
  assertCurrentActionTargets(targets, run, scenario, 'player_kp.action.targets')
  assertConfiguredClaims(claims, scenario)
  for (const clue of requiredArray(output.clueJudgements, 'player_kp.clueJudgements')) {
    const item = requiredRecord(clue, 'player_kp.clueJudgements[]')
    assertKnownClue(item.clueId, scenario, 'player_kp.clueJudgements[].clueId')
  }
  assertKnownSceneObjects(output.sceneResult, run, scenario, 'player_kp.sceneResult')
  const npcIds = reactionSceneNpcIds(output.action, run, scenario)
  const npcId = output.npcDirective?.npcId
  const npcReaction = output.responsePolicy?.npcReaction
  if (!npcIds.size && npcReaction !== 'none') {
    throw new AgentOutputValidationError('player_kp.responsePolicy.npcReaction must be none when the current scene has no NPC')
  }
  if (npcIds.size > 1 && npcId === '') {
    throw new AgentOutputValidationError('player_kp.npcDirective.npcId must select an active NPC when the current scene has multiple NPCs')
  }
  if (npcIds.size && npcId === '' && npcReaction !== 'none') {
    throw new AgentOutputValidationError('player_kp.npcDirective.npcId must select an active NPC when an NPC reaction is enabled')
  }
  if (npcId !== '' && !npcIds.has(npcId)) {
    throw new AgentOutputValidationError(`player_kp.npcDirective.npcId is not active in the reaction scene: ${String(npcId)}`)
  }
  if (!npcIds.size && npcId !== '') {
    throw new AgentOutputValidationError('player_kp.npcDirective.npcId must be empty when the scenario has no NPC')
  }
  authorizeAgentDelta('player_kp', output.judgementSuggestion, { run, scenario, action, actor_id: 'player' })
  return output
}

function assertConfiguredClaims(claims, scenario) {
  const configured = new Set(scenario.kpGuide.claims.map(claim => claim.id))
  for (const claim of claims) {
    if (!configured.has(claim)) {
      throw new AgentOutputValidationError(`player_kp.action.claims contains unknown claim: ${claim}`)
    }
  }
}

/**
 * @param {AnyRecord} output
 * @param {{ run: AnyRecord, scenario: AnyRecord, npc_id: string }} context
 */
export function assertNpcActorOutputContext(output, { run, scenario, npc_id, player_judgement = null }) {
  if (!currentSceneNpcIds(run, scenario).has(npc_id)) {
    throw new Error(`npc Actor is not active in the current scene: ${npc_id}`)
  }
  const detectorTypes = new Set(scenario.kpGuide.npcActionDetectors.map(detector => detector.actionType))
  const proposalKeys = new Set()
  for (const rawAction of requiredArray(output.proposedNpcActions, 'npc.proposedNpcActions')) {
    const action = requiredRecord(rawAction, 'npc.proposedNpcActions[]')
    const type = requiredText(action.type, 'npc.proposedNpcActions[].type')
    if (type === 'none') throw new AgentOutputValidationError('npc.proposedNpcActions must use [] instead of type=none')
    const targetId = requiredText(action.targetId, 'npc.proposedNpcActions[].targetId')
    const proposalKey = JSON.stringify([type, targetId])
    if (proposalKeys.has(proposalKey)) {
      throw new AgentOutputValidationError(`npc.proposedNpcActions contains duplicate proposal: ${type}:${targetId}`)
    }
    proposalKeys.add(proposalKey)
    assertCurrentActionTargets([targetId], run, scenario, 'npc.proposedNpcActions[].targetId')
    if (action.candidate_delta === undefined) {
      if (!detectorTypes.has(type)) {
        throw new AgentOutputValidationError(`npc.proposedNpcActions[].candidate_delta is required for non-detector action: ${type}`)
      }
    } else {
      const candidateDelta = authorizeAgentDelta('npc_actor_candidate', action.candidate_delta, {
        run,
        scenario,
        action: { ...action, targets: [targetId] },
        actor_id: npc_id,
      })
      assertNpcDeltaPreservesCommittedPlayerOutcome(
        candidateDelta,
        player_judgement,
        'npc_actor_candidate',
      )
      if (!detectorTypes.has(type) && deltaMutationPaths(candidateDelta).size === 0) {
        throw new AgentOutputValidationError(`npc.proposedNpcActions[].candidate_delta must contain a mutation for non-detector action: ${type}`)
      }
    }
  }
  return output
}

/**
 * @param {AnyRecord} output
 * @param {{ run: AnyRecord, scenario: AnyRecord, proposed_actions: AnyRecord[], npc_id: string }} context
 * @returns {{ approved_proposals: AnyRecord[], suggestion: AnyRecord }}
 */
export function npcActionKpOutputContext(output, {
  run,
  scenario,
  proposed_actions,
  npc_id: expectedNpcId,
  player_judgement = null,
}) {
  const npcIds = knownNpcIds(scenario)
  const npcId = requiredText(output.npcId, 'npc_action_kp.npcId')
  if (!npcIds.has(npcId)) throw new AgentOutputValidationError(`npc_action_kp.npcId is unknown: ${npcId}`)
  if (typeof expectedNpcId !== 'string' || !expectedNpcId.trim()) {
    throw new Error('npc_action_kp.expectedNpcId must be a non-empty string')
  }
  if (npcId !== expectedNpcId.trim()) {
    throw new AgentOutputValidationError(`npc_action_kp.npcId must match the NPC Actor: ${expectedNpcId}`)
  }

  const proposed = requiredArray(proposed_actions, 'npc_action_kp.proposed_actions')
    .map((rawAction, index) => {
      const item = requiredRecord(rawAction, `npc_action_kp.proposed_actions[${index}]`)
      return {
        item,
        type: requiredText(item.type, `npc_action_kp.proposed_actions[${index}].type`),
        targetId: requiredText(item.targetId, `npc_action_kp.proposed_actions[${index}].targetId`),
      }
    })
  const approved = requiredArray(output.approvedNpcActions, 'npc_action_kp.approvedNpcActions')
  if (approved.length > 1) throw new AgentOutputValidationError('npc_action_kp may approve at most one NPC action per turn')
  const approvalKeys = new Set()
  const approvedProposals = []
  for (const [index, rawAction] of approved.entries()) {
    const item = requiredRecord(rawAction, `npc_action_kp.approvedNpcActions[${index}]`)
    const type = requiredText(item.type, `npc_action_kp.approvedNpcActions[${index}].type`)
    const targetId = requiredText(item.targetId, `npc_action_kp.approvedNpcActions[${index}].targetId`)
    const key = JSON.stringify([type, targetId])
    if (approvalKeys.has(key)) {
      throw new AgentOutputValidationError(`npc_action_kp.approvedNpcActions contains duplicate approval: ${type}:${targetId}`)
    }
    approvalKeys.add(key)
    const matches = proposed.filter(candidate => candidate.type === type && candidate.targetId === targetId)
    if (matches.length === 0) {
      throw new AgentOutputValidationError(`npc_action_kp approved action has no matching proposal: ${type}:${targetId}`)
    }
    if (matches.length > 1) {
      throw new Error(`npc_action_kp approved action matches multiple proposals: ${type}:${targetId}`)
    }
    approvedProposals.push(matches[0])
  }

  const approvedAction = approvedProposals.length
    ? { ...approvedProposals[0].item, targets: [approvedProposals[0].targetId] }
    : { targets: [] }
  const suggestion = authorizeAgentDelta('npc_action_kp', output.judgementSuggestion, {
    run,
    scenario,
    action: approvedAction,
    actor_id: npcId,
  })
  assertNpcDeltaPreservesCommittedPlayerOutcome(
    suggestion,
    player_judgement,
    'npc_action_kp',
  )
  if (suggestion.valid === false && approved.length) {
    throw new AgentOutputValidationError('npc_action_kp.approvedNpcActions must be empty when valid=false')
  }
  if (!approved.length && deltaMutationPaths(suggestion).size) {
    throw new AgentOutputValidationError('npc_action_kp cannot return delta without an approved NPC action')
  }
  if (!approved.length) assertEmptyNpcActionSceneResult(output.sceneResult)

  const candidateEffects = new Map()
  for (const proposal of approvedProposals) {
    if (proposal.item.candidate_delta === undefined) continue
    const candidateDelta = authorizeAgentDelta('npc_actor_candidate', proposal.item.candidate_delta, {
      run,
      scenario,
      action: { ...proposal.item, targets: [proposal.targetId] },
      actor_id: npcId,
    })
    assertNpcDeltaPreservesCommittedPlayerOutcome(
      candidateDelta,
      player_judgement,
      'npc_actor_candidate',
    )
    for (const [path, value] of deltaMutationEntries(candidateDelta)) candidateEffects.set(path, value)
  }
  for (const [path, value] of deltaMutationEntries(suggestion)) {
    if (!candidateEffects.has(path)) {
      throw new AgentOutputValidationError(`npc_action_kp delta was not proposed by NPC Actor: ${path}`)
    }
    assertCandidateEffectValue(path, candidateEffects.get(path), value)
  }

  const approvedTargets = new Set(approvedProposals.map(proposal => proposal.targetId))
  for (const objectId of Object.keys(suggestion.objectStateChanges || {})) {
    if (!approvedTargets.has(objectId)) {
      throw new AgentOutputValidationError(`npc_action_kp changed an object outside approved targets: ${objectId}`)
    }
  }
  for (const changedNpcId of Object.keys(suggestion.npcStateChanges || {})) {
    if (changedNpcId !== npcId && !approvedTargets.has(changedNpcId)) {
      throw new AgentOutputValidationError(`npc_action_kp changed an NPC outside approved targets: ${changedNpcId}`)
    }
  }
  assertKnownSceneObjects(output.sceneResult, run, scenario, 'npc_action_kp.sceneResult')
  return {
    approved_proposals: approvedProposals.map(proposal => proposal.item),
    suggestion,
  }
}

/**
 * @param {AnyRecord} output
 * @param {{ run: AnyRecord, scenario: AnyRecord, proposed_actions: AnyRecord[], npc_id: string }} context
 */
export function assertNpcActionKpOutputContext(output, context) {
  npcActionKpOutputContext(output, context)
  return output
}

/**
 * Prevent a later NPC delta from writing a state path already committed by the
 * Player KP plus deterministic action rules in the same turn.
 *
 * Additive knowledge, evidence and stats deliberately remain independent NPC
 * consequences and therefore are not part of this lock set.
 *
 * @param {AnyRecord} delta
 * @param {AnyRecord|null} playerJudgement
 * @param {string} source
 */
export function assertNpcDeltaPreservesCommittedPlayerOutcome(delta, playerJudgement, source) {
  if (playerJudgement === null || playerJudgement.valid !== true) return delta
  const committedPaths = committedOutcomePaths(playerJudgement)
  for (const path of committedOutcomePaths(delta)) {
    if (!committedPaths.has(path)) continue
    throw new AgentOutputValidationError(
      `${source} cannot overwrite the committed player outcome: ${formatCommittedOutcomePath(path)}`,
    )
  }
  return delta
}

/** @param {AnyRecord} delta */
export function markAuthorizedDelta(delta) {
  Object.defineProperty(delta, AUTHORIZED_DELTA, {
    value: true,
    enumerable: false,
    configurable: false,
  })
  return delta
}

/** @param {AnyRecord} delta */
export function assertAuthorizedDelta(delta) {
  if (delta?.[AUTHORIZED_DELTA] !== true) throw new Error('applyDelta requires an authorized delta')
}

/**
 * Trusted scenario effects still receive the application brand, but are deliberately
 * kept separate from model-writer authorization. Publish checks will own their registry audit.
 *
 * @param {AnyRecord} delta
 */
export function authorizeConfiguredDelta(delta) {
  return markAuthorizedDelta(delta)
}

function writersForSource(source) {
  switch (source) {
    case 'player_kp':
      return PLAYER_KP_WRITERS
    case 'npc_action_kp':
      return NPC_ACTION_KP_WRITERS
    case 'npc_actor_candidate':
      return NPC_ACTION_KP_WRITERS
    default:
      throw new Error(`Unhandled agent delta source: ${source}`)
  }
}

function validateStatChanges(changes, scenario, source, action) {
  if (changes === undefined) return
  const value = requiredRecord(changes, `${source}.statChanges`)
  const entries = safeEntries(value, `${source}.statChanges`)
  // 只有真的要改数值才需要对齐 target：agent 的结构化输出即使什么都不改也会带一个空
  // statChanges 对象，按「字段存在」而不是按「有没有条目」判就会把「没打算改」误判成越权。
  if (source !== 'player_kp' && entries.length > 0) assertActionTarget(action, 'player', `${source}.statChanges`)
  const knownStats = new Set([
    ...Object.keys(scenario.initialStats),
    ...scenario.statDefinitions.map(definition => definition.id),
  ])
  for (const [id, amount] of entries) {
    if (!knownStats.has(id)) throw new AgentOutputValidationError(`${source}.statChanges contains unknown stat: ${id}`)
    if (typeof amount !== 'number' || !Number.isFinite(amount) || amount < -100 || amount > 100) {
      throw new AgentOutputValidationError(`${source}.statChanges.${id} must be a finite number between -100 and 100`)
    }
  }
}

function validateFlags(flags, scenario, writers, source) {
  if (flags === undefined) return
  for (const [id, value] of safeEntries(requiredRecord(flags, `${source}.flagsSet`), `${source}.flagsSet`)) {
    const entry = requiredRegistryEntry(scenario, 'global_flag', [id], `${source}.flagsSet.${id}`)
    assertRegistryWrite(entry, value, writers, `${source}.flagsSet.${id}`)
  }
}

function validateEvidence(evidence, scenario, source) {
  if (evidence === undefined) return
  const ids = requiredStringArray(evidence, `${source}.evidenceAdded`)
  if ((source === 'npc_action_kp' || source === 'npc_actor_candidate') && ids.length) {
    throw new AgentOutputValidationError(`${source}.judgementSuggestion.evidenceAdded must come from a configured npcActionDetector`)
  }
  for (const id of ids) assertKnownClue(id, scenario, `${source}.evidenceAdded`)
}

function validateObjectChanges(changes, run, scenario, writers, source, action) {
  if (changes === undefined) return
  const objectIds = knownObjectIds(run, scenario)
  for (const [id, rawPatch] of safeEntries(requiredRecord(changes, `${source}.objectStateChanges`), `${source}.objectStateChanges`)) {
    if (!objectIds.has(id)) throw new AgentOutputValidationError(`${source}.objectStateChanges contains unknown object: ${id}`)
    assertObjectAccessible(id, run, `${source}.objectStateChanges.${id}`)
    const patch = requiredRecord(rawPatch, `${source}.objectStateChanges.${id}`)
    for (const [field, value] of safeEntries(patch, `${source}.objectStateChanges.${id}`)) {
      if (field === 'flags') {
        assertActionTarget(action, id, `${source}.objectStateChanges.${id}`)
        for (const [flag, flagValue] of safeEntries(requiredRecord(value, `${source}.objectStateChanges.${id}.flags`), `${source}.objectStateChanges.${id}.flags`)) {
          const path = [id, 'flags', flag]
          const entry = requiredRegistryEntry(scenario, 'object_state', path, `${source}.objectStateChanges.${path.join('.')}`)
          assertRegistryWrite(entry, flagValue, writers, `${source}.objectStateChanges.${path.join('.')}`)
        }
        continue
      }
      const path = [id, field]
      assertObjectChangeTarget(action, id, run, path, value, `${source}.objectStateChanges.${id}`)
      const entry = requiredRegistryEntry(scenario, 'object_state', path, `${source}.objectStateChanges.${path.join('.')}`)
      assertRegistryWrite(entry, value, writers, `${source}.objectStateChanges.${path.join('.')}`)
    }
  }
}

function validateNpcChanges(changes, run, scenario, writers, source, action, actorId) {
  if (changes === undefined) return
  const npcIds = knownNpcIds(scenario)
  const activeNpcIds = new Set(activeNpcIdsForLocation(scenario, run))
  for (const [id, rawPatch] of safeEntries(requiredRecord(changes, `${source}.npcStateChanges`), `${source}.npcStateChanges`)) {
    if (!npcIds.has(id)) throw new AgentOutputValidationError(`${source}.npcStateChanges contains unknown NPC: ${id}`)
    if (!activeNpcIds.has(id)) {
      throw new AgentOutputValidationError(`${source}.npcStateChanges.${id} is outside the current scene`)
    }
    if (id !== actorId) assertActionTarget(action, id, `${source}.npcStateChanges.${id}`)
    const patch = requiredRecord(rawPatch, `${source}.npcStateChanges.${id}`)
    for (const [field, value] of safeEntries(patch, `${source}.npcStateChanges.${id}`)) {
      if (field === 'relationshipFlags') {
        for (const [flag, flagValue] of safeEntries(requiredRecord(value, `${source}.npcStateChanges.${id}.relationshipFlags`), `${source}.npcStateChanges.${id}.relationshipFlags`)) {
          const path = [id, 'relationshipFlags', flag]
          const entry = requiredRegistryEntry(scenario, 'npc_relationship_flag', path, `${source}.npcStateChanges.${path.join('.')}`)
          assertRegistryWrite(entry, flagValue, writers, `${source}.npcStateChanges.${path.join('.')}`)
        }
        continue
      }
      if (field === 'knownFacts' || field === 'revealedSecrets') {
        for (const fact of requiredStringArray(value, `${source}.npcStateChanges.${id}.${field}`)) {
          const path = [id, field, fact]
          const entry = requiredRegistryEntry(scenario, 'npc_state', path, `${source}.npcStateChanges.${id}.${field}`)
          assertRegistryWrite(entry, fact, writers, `${source}.npcStateChanges.${id}.${field}`)
        }
        continue
      }
      const path = [id, field]
      const entry = requiredRegistryEntry(scenario, 'npc_state', path, `${source}.npcStateChanges.${path.join('.')}`)
      assertRegistryWrite(entry, value, writers, `${source}.npcStateChanges.${path.join('.')}`)
      if (field === 'locationId' && !knownLocationIds(scenario).has(value)) {
        throw new AgentOutputValidationError(`${source}.npcStateChanges.${id}.locationId is unknown: ${String(value)}`)
      }
      if (field === 'locationId') {
        assertActionTarget(action, value, `${source}.npcStateChanges.${id}.locationId`)
      }
    }
    const hasLocationChange = Object.hasOwn(patch, 'locationId')
    const nextLocationId = hasLocationChange ? patch.locationId : run.npcStates[id].locationId
    if (hasLocationChange) {
      assertNpcLocationTransition(run, scenario, id, nextLocationId, source, action)
      assertNpcLocationChangePosition(run, scenario, id, patch, nextLocationId, source)
    }
    if (Object.hasOwn(patch, 'position')) {
      assertNpcPositionTransition(run, scenario, id, patch.position, nextLocationId, source)
    }
  }
}

function assertNpcLocationTransition(run, scenario, npcId, nextLocationId, source, action) {
  const currentLocationId = run.npcStates[npcId].locationId
  if (nextLocationId === currentLocationId) return
  const passability = locationConnectionPassability(
    scenario,
    run,
    currentLocationId,
    nextLocationId,
    action,
  )
  if (!passability.passable) {
    throw new AgentOutputValidationError(
      `${source}.npcStateChanges.${npcId}.locationId transition is not passable: ${currentLocationId} -> ${nextLocationId}; ${passability.reason}`,
    )
  }
}

function assertNpcLocationChangePosition(run, scenario, npcId, patch, nextLocationId, source) {
  const npcState = run.npcStates[npcId]
  if (nextLocationId === npcState.locationId || Object.hasOwn(patch, 'position')) return
  const targetLocation = locationMap(scenario)[nextLocationId]
  const targetInitialState = npcInitialStateForLocation(scenario, targetLocation, npcId)
  const hasPositionSemantics = npcState.position !== undefined
    || targetInitialState.position !== undefined
    || targetLocation.positionTransitions?.[npcId] !== undefined
  if (hasPositionSemantics) {
    throw new AgentOutputValidationError(
      `${source}.npcStateChanges.${npcId}.position is required when changing location: ${npcState.locationId} -> ${nextLocationId}`,
    )
  }
}

function assertNpcPositionTransition(run, scenario, npcId, nextPosition, nextLocationId, source) {
  const npcState = run.npcStates[npcId]
  if (nextLocationId !== npcState.locationId) {
    const targetLocation = locationMap(scenario)[nextLocationId]
    const targetInitialState = npcInitialStateForLocation(scenario, targetLocation, npcId)
    if (targetInitialState.position === undefined) {
      throw new AgentOutputValidationError(
        `${source}.npcStateChanges.${npcId}.position cannot accompany a location change because ${nextLocationId} has no configured initial position`,
      )
    }
    if (nextPosition !== targetInitialState.position) {
      throw new AgentOutputValidationError(
        `${source}.npcStateChanges.${npcId}.position must use the target location initial position: ${String(targetInitialState.position)}; received ${String(nextPosition)}`,
      )
    }
    return
  }
  const graph = locationMap(scenario)[nextLocationId]?.positionTransitions?.[npcId]
  if (graph === undefined || nextPosition === npcState.position) return
  const allowed = graph[npcState.position] || []
  if (!allowed.includes(nextPosition)) {
    throw new AgentOutputValidationError(
      `${source}.npcStateChanges.${npcId}.position transition is not allowed: ${String(npcState.position)} -> ${String(nextPosition)}`,
    )
  }
}

function npcInitialStateForLocation(scenario, location, npcId) {
  return mergeNpcInitialState(
    scenario.initialNpcStates[npcId],
    location.initialNpcStates?.[npcId],
  )
}

function validateInventory(changes, run, scenario, source, action) {
  if (changes === undefined) return
  const value = requiredRecord(changes, `${source}.inventoryChanges`)
  assertKnownKeys(value, INVENTORY_FIELDS, `${source}.inventoryChanges`)
  const add = requiredStringArray(value.add, `${source}.inventoryChanges.add`)
  const remove = requiredStringArray(value.remove, `${source}.inventoryChanges.remove`)
  const knownItems = new Set([...scenario.objects.map(object => object.id), ...run.inventory])
  for (const id of add) {
    if (!knownItems.has(id)) throw new AgentOutputValidationError(`${source}.inventoryChanges.add contains unknown item: ${id}`)
    if (!run.inventory.includes(id)) assertObjectAccessible(id, run, `${source}.inventoryChanges.add.${id}`)
    assertActionTarget(action, id, `${source}.inventoryChanges.add.${id}`)
  }
  for (const id of remove) {
    if (!run.inventory.includes(id)) {
      throw new AgentOutputValidationError(`${source}.inventoryChanges.remove contains item not held by player: ${id}`)
    }
    assertActionTarget(action, id, `${source}.inventoryChanges.remove.${id}`)
  }
  for (const id of add) {
    if (remove.includes(id)) {
      throw new AgentOutputValidationError(`${source}.inventoryChanges cannot add and remove the same item: ${id}`)
    }
  }
}

function validateLocationChanges(suggestion, scenario, action, source) {
  if (source !== 'player_kp' && suggestion.locationChange !== undefined) {
    throw new AgentOutputValidationError(`${source} cannot change the player's location`)
  }
  if (suggestion.locationChange !== undefined) {
    const id = requiredText(suggestion.locationChange, `${source}.locationChange`)
    if (!knownLocationIds(scenario).has(id)) throw new AgentOutputValidationError(`${source}.locationChange is unknown: ${id}`)
    if (action?.intent !== 'move' || !action.targets.includes(id)) {
      throw new AgentOutputValidationError(`${source}.locationChange must match a move action target: ${id}`)
    }
  }
}

function validateNotes(notes, source) {
  if (notes !== undefined) requiredStringArray(notes, `${source}.notes`)
}

function requiredRegistryEntry(scenario, scope, path, field) {
  const matches = scenario.stateRegistry
    .filter(entry => entry.scope === scope && registryPatternMatches(entry.id, path))
    .map(entry => ({ entry, specificity: entry.id.split('.').filter(segment => segment !== '*').length }))
    .sort((left, right) => right.specificity - left.specificity)
  if (!matches.length) throw new AgentOutputValidationError(`${field} is not registered in stateRegistry`)
  const best = matches[0]
  const equallySpecific = matches.filter(match => match.specificity === best.specificity)
  if (equallySpecific.length !== 1) {
    throw new Error(`${field} matches multiple equally specific stateRegistry entries`)
  }
  return best.entry
}

function registryPatternMatches(pattern, path) {
  const parts = pattern.split('.')
  if (parts.length !== path.length) return false
  return parts.every((part, index) => part === '*' || part === path[index])
}

function assertRegistryWrite(entry, value, writers, field) {
  assertRegistryValueType(entry, value, field)
  if (writers === null) return
  if (!Array.isArray(entry.writers)) throw new Error(`stateRegistry writers must be an array: ${entry.id}`)
  if (!entry.writers.some(writer => writers.has(writer))) {
    throw new AgentOutputValidationError(`${field} is not writable by this Agent; stateRegistry entry: ${entry.id}`)
  }
}

function assertRegistryValueType(entry, value, field) {
  switch (entry.valueType) {
    case 'boolean':
      if (typeof value === 'boolean') return
      break
    case 'number':
      if (typeof value === 'number' && Number.isFinite(value)) return
      break
    case 'string':
      if (typeof value === 'string') return
      break
    default:
      throw new Error(`Unhandled stateRegistry valueType: ${String(entry.valueType)}, id: ${entry.id}`)
  }
  throw new AgentOutputValidationError(`${field} must be ${entry.valueType}`)
}

function deltaMutationPaths(delta) {
  return new Set(deltaMutationEntries(delta).keys())
}

function committedOutcomePaths(delta) {
  const paths = new Set()
  for (const id of Object.keys(delta.flagsSet || {})) addCommittedOutcomePath(paths, ['flags', id])
  for (const [id, patch] of Object.entries(delta.objectStateChanges || {})) {
    for (const [field, value] of Object.entries(patch)) {
      if (field === 'flags') {
        for (const flag of Object.keys(value)) addCommittedOutcomePath(paths, ['objectStates', id, 'flags', flag])
      } else if (isStateScalar(value)) {
        addCommittedOutcomePath(paths, ['objectStates', id, field])
      }
    }
  }
  for (const [id, patch] of Object.entries(delta.npcStateChanges || {})) {
    for (const [field, value] of Object.entries(patch)) {
      if (field === 'knownFacts' || field === 'revealedSecrets') continue
      if (field === 'relationshipFlags') {
        for (const flag of Object.keys(value)) addCommittedOutcomePath(paths, ['npcStates', id, 'relationshipFlags', flag])
      } else if (isStateScalar(value)) {
        addCommittedOutcomePath(paths, ['npcStates', id, field])
      }
    }
  }
  for (const field of ['add', 'remove']) {
    for (const id of delta.inventoryChanges?.[field] || []) addCommittedOutcomePath(paths, ['inventory', id])
  }
  if (delta.locationChange !== undefined && delta.locationChange !== null) {
    addCommittedOutcomePath(paths, ['currentLocation'])
  }
  return paths
}

function addCommittedOutcomePath(paths, path) {
  paths.add(JSON.stringify(path))
}

function formatCommittedOutcomePath(path) {
  return JSON.parse(path).join('.')
}

function isStateScalar(value) {
  return value === null || ['string', 'number', 'boolean'].includes(typeof value)
}

function deltaMutationEntries(delta) {
  const entries = new Map()
  addRecordEntries(entries, 'statChanges', delta.statChanges)
  addRecordEntries(entries, 'flagsSet', delta.flagsSet)
  for (const id of delta.evidenceAdded || []) entries.set(`evidenceAdded.${id}`, true)
  addNestedPatchEntries(entries, 'objectStateChanges', delta.objectStateChanges)
  addNestedPatchEntries(entries, 'npcStateChanges', delta.npcStateChanges)
  for (const field of ['add', 'remove']) {
    for (const id of delta.inventoryChanges?.[field] || []) entries.set(`inventoryChanges.${field}.${id}`, true)
  }
  if (delta.locationChange !== undefined) entries.set('locationChange', delta.locationChange)
  return entries
}

function addRecordEntries(entries, prefix, value) {
  for (const [key, item] of Object.entries(value || {})) entries.set(`${prefix}.${key}`, item)
}

function addNestedPatchEntries(entries, prefix, value) {
  for (const [id, rawPatch] of Object.entries(value || {})) {
    const patch = requiredRecord(rawPatch, `${prefix}.${id}`)
    for (const [field, nested] of Object.entries(patch)) {
      if (Array.isArray(nested)) {
        for (const item of nested) entries.set(`${prefix}.${id}.${field}.${item}`, true)
      } else if (nested && typeof nested === 'object') {
        for (const [key, item] of Object.entries(nested)) entries.set(`${prefix}.${id}.${field}.${key}`, item)
      } else {
        entries.set(`${prefix}.${id}.${field}`, nested)
      }
    }
  }
}

function assertCandidateEffectValue(path, candidate, approved) {
  if (path.startsWith('statChanges.') && typeof candidate === 'number' && typeof approved === 'number') {
    if (
      approved === 0 ||
      (
        candidate !== 0 &&
        Math.sign(approved) === Math.sign(candidate) &&
        Math.abs(approved) <= Math.abs(candidate)
      )
    ) return
  } else if (Object.is(candidate, approved)) {
    return
  }
  throw new AgentOutputValidationError(`npc_action_kp changed the value proposed by NPC Actor: ${path}`)
}

function assertKnownSceneObjects(sceneResult, run, scenario, field) {
  const scene = requiredRecord(sceneResult, field)
  const objectIds = knownObjectIds(run, scenario)
  for (const id of requiredStringArray(scene.discoveredObjectIds, `${field}.discoveredObjectIds`)) {
    if (!objectIds.has(id)) {
      throw new AgentOutputValidationError(`${field}.discoveredObjectIds contains unknown object: ${id}`)
    }
    assertObjectAccessible(id, run, `${field}.discoveredObjectIds.${id}`)
  }
}

function assertEmptyNpcActionSceneResult(sceneResult) {
  const scene = requiredRecord(sceneResult, 'npc_action_kp.sceneResult')
  if (
    scene.narration !== '' ||
    requiredArray(scene.visibleFindings, 'npc_action_kp.sceneResult.visibleFindings').length > 0 ||
    requiredArray(scene.discoveredObjectIds, 'npc_action_kp.sceneResult.discoveredObjectIds').length > 0
  ) {
    throw new AgentOutputValidationError('npc_action_kp.sceneResult must be empty without an approved NPC action')
  }
}

function assertObjectAccessible(id, run, field) {
  if (!Object.hasOwn(run.objectStates, id)) throw new Error(`${field} has no runtime object state`)
  if (run.inventory.includes(id)) return
  if (run.objectStates[id].location !== run.currentLocation) {
    throw new AgentOutputValidationError(`${field} is outside the current scene`)
  }
}

function assertKnownClue(id, scenario, field) {
  if (!scenario.clues.some(clue => clue.id === id)) {
    throw new AgentOutputValidationError(`${field} contains unknown clue: ${String(id)}`)
  }
}

function knownTargetIds(run, scenario) {
  return new Set([
    'player',
    ...knownObjectIds(run, scenario),
    ...knownNpcIds(scenario),
    ...knownLocationIds(scenario),
  ])
}

function currentActionTargetIds(run, scenario) {
  const objectIds = Object.keys(run.objectStates).filter(id => (
    run.inventory.includes(id) || run.objectStates[id].location === run.currentLocation
  ))
  return new Set([
    'player',
    ...objectIds,
    ...currentSceneNpcIds(run, scenario),
    ...knownLocationIds(scenario),
  ])
}

function currentSceneNpcIds(run, scenario) {
  const known = knownNpcIds(scenario)
  return new Set(activeNpcIdsForLocation(scenario, run).filter(id => known.has(id)))
}

function reactionSceneNpcIds(action, run, scenario) {
  if (action.intent !== 'move') return currentSceneNpcIds(run, scenario)
  const locations = knownLocationIds(scenario)
  const destination = action.targets.find(target => locations.has(target))
  if (destination === undefined) return currentSceneNpcIds(run, scenario)
  const known = knownNpcIds(scenario)
  return new Set(activeNpcIdsForLocation(scenario, {
    currentLocation: destination,
    npcStates: run.npcStates,
  }).filter(id => known.has(id)))
}

function assertCurrentActionTargets(targets, run, scenario, field) {
  const known = knownTargetIds(run, scenario)
  const current = currentActionTargetIds(run, scenario)
  for (const target of targets) {
    if (!known.has(target)) throw new AgentOutputValidationError(`${field} contains unknown id: ${target}`)
    if (!current.has(target)) throw new AgentOutputValidationError(`${field} is outside the current scene: ${target}`)
  }
}

function knownObjectIds(run, scenario) {
  return new Set([...Object.keys(run.objectStates), ...scenario.objects.map(object => object.id)])
}

function knownNpcIds(scenario) {
  return new Set(scenario.npcs.map(npc => npc.id))
}

function assertKnownKeys(value, allowed, field) {
  for (const key of Object.keys(value)) {
    assertSafeKey(key, field)
    if (!allowed.has(key)) throw new AgentOutputValidationError(`${field} contains unknown field: ${key}`)
  }
}

function assertActionTarget(action, id, field) {
  if (!action || !Array.isArray(action.targets) || !action.targets.includes(id)) {
    throw new AgentOutputValidationError(`${field} must target the entity changed by this action`)
  }
}

function assertObjectChangeTarget(action, id, run, path, value, field) {
  if (action?.targets?.includes(id)) return
  if (
    action?.intent === 'investigate' &&
    action.targets?.includes(run.currentLocation) &&
    path.length === 2 &&
    path[1] === 'known' &&
    value === true
  ) {
    return
  }
  throw new AgentOutputValidationError(`${field} must target the entity changed by this action`)
}

function safeEntries(value, field) {
  for (const key of Object.keys(value)) assertSafeKey(key, field)
  return Object.entries(value)
}

function assertSafeKey(key, field) {
  if (DANGEROUS_KEYS.has(key)) throw new AgentOutputValidationError(`${field} contains unsafe key: ${key}`)
}

function requiredRecord(value, field) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new AgentOutputValidationError(`${field} must be an object`)
  }
  return value
}

function requiredArray(value, field) {
  if (!Array.isArray(value)) throw new AgentOutputValidationError(`${field} must be an array`)
  return value
}

function requiredStringArray(value, field) {
  if (!Array.isArray(value) || value.some(item => typeof item !== 'string' || !item.trim())) {
    throw new AgentOutputValidationError(`${field} must be an array of non-empty strings`)
  }
  return value
}

function requiredText(value, field) {
  if (typeof value !== 'string' || !value.trim()) {
    throw new AgentOutputValidationError(`${field} must be a non-empty string`)
  }
  return value.trim()
}
